/**
 * ExpirationPill — canonical expiration alert status color config and reusable pill component.
 *
 * Single source of truth for all expiration-related badge/pill styling throughout the app.
 * Used for: medications, authorizations, licenses, care team access, etc.
 *
 * Color-coded by urgency:
 *   - Red: Expired or critically urgent (< 7 days)
 *   - Orange: Very urgent (7-14 days)
 *   - Yellow: Moderately urgent (14-30 days)
 *   - Green: Healthy (> 30 days)
 *   - Gray: No expiration date set
 *
 * Usage (component):
 *   <ExpirationPill status="red" text="Expires in 3d" />
 *   <ExpirationPill status="expired" text="Expired" />
 *   <ExpirationPill status="green" text="2026-05-15" />
 *
 * Usage (utilities):
 *   getExpirationConfig('red')         → full config object
 *   getExpirationPillClassName('yellow') → "bg-yellow-100 border-yellow-200 text-yellow-800 ..."
 */

import React from 'react';

// ─── Config type ──────────────────────────────────────────────────────────────

export interface ExpirationConfig {
  /** Light pill background (e.g. bg-red-100) */
  bg: string;
  /** Pill text color (e.g. text-red-800) */
  text: string;
  /** Pill border color (e.g. border-red-200) */
  border: string;
}

// ─── Canonical map ────────────────────────────────────────────────────────────

const BASE = 'font-medium text-xs rounded-full border inline-flex items-center px-2.5 py-0.5';

const EXPIRATION_MAP: Record<string, ExpirationConfig> = {
  // ── Red: Expired or critically urgent ────────────────────────────────────────
  RED: {
    bg: 'bg-red-100',
    text: 'text-red-800',
    border: 'border-red-200',
  },
  EXPIRED: {
    bg: 'bg-red-100',
    text: 'text-red-800',
    border: 'border-red-200',
  },

  // ── Orange: Very urgent (7-14 days) ────────────────────────────────────────
  ORANGE: {
    bg: 'bg-orange-100',
    text: 'text-orange-800',
    border: 'border-orange-200',
  },

  // ── Yellow: Moderately urgent (14-30 days) ─────────────────────────────────
  YELLOW: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-800',
    border: 'border-yellow-200',
  },

  // ── Green: Healthy (> 30 days) ────────────────────────────────────────────
  GREEN: {
    bg: 'bg-green-100',
    text: 'text-green-800',
    border: 'border-green-200',
  },

  // ── Gray: No expiration date ───────────────────────────────────────────────
  GRAY: {
    bg: 'bg-gray-100',
    text: 'text-gray-600',
    border: 'border-gray-200',
  },
  NONE: {
    bg: 'bg-gray-100',
    text: 'text-gray-600',
    border: 'border-gray-200',
  },
};

const DEFAULT_CONFIG: ExpirationConfig = {
  bg: 'bg-gray-100',
  text: 'text-gray-600',
  border: 'border-gray-200',
};

// ─── Utility functions ────────────────────────────────────────────────────────

/**
 * Look up the canonical config for an expiration level.
 * Normalises to uppercase; falls back to gray for unknown statuses.
 *
 * @param status - One of: 'red' | 'orange' | 'yellow' | 'green' | 'gray' | 'expired' | 'none'
 */
export function getExpirationConfig(
  status: string
): ExpirationConfig {
  if (!status) return DEFAULT_CONFIG;
  const key = status.trim().toUpperCase();
  return EXPIRATION_MAP[key] ?? DEFAULT_CONFIG;
}

/**
 * Returns the full Tailwind class string for an expiration pill.
 *
 * @example
 *   className={`${getExpirationPillClassName('red')}`}
 */
export function getExpirationPillClassName(status: string): string {
  const config = getExpirationConfig(status);
  return `${config.bg} ${config.text} ${config.border} ${BASE}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface ExpirationPillProps {
  /** Expiration level: 'red' | 'orange' | 'yellow' | 'green' | 'gray' | 'expired' | 'none' */
  status: string;
  /** Display text content (e.g. "Expired", "Expires in 5d", date string) */
  text: React.ReactNode;
  /** Extra Tailwind classes appended to the root element */
  className?: string;
}

/**
 * ExpirationPill — canonical expiration alert badge component.
 *
 * Renders as: font-medium · text-xs · rounded-full · border + status-specific
 * light background, colored text, and matching border.
 *
 * Standardized layout: inline-flex items-center · px-2.5 py-0.5
 */
export function ExpirationPill({
  status,
  text,
  className = '',
}: ExpirationPillProps) {
  return (
    <span
      className={`${getExpirationPillClassName(status)} ${className}`.trim()}
    >
      {text}
    </span>
  );
}

export default ExpirationPill;
